import { Component, Prop, Vue } from 'vue-property-decorator';
import appStore from '@/store/app';
import usersStore from '@/store/users';
import authenticator from '@/store/authentication';
import { User as ApplicationUser, Role, Partner } from '@/models';
import CardComponent from '@/components/CardComponent';
import EditToolbar from '@/components/EditToolbar';
import ChangePassword from '@/components/ChangePassword';
import partnersService from '@/services/PartnersService';
import { Guid } from '@/types';
import { debounce } from '@/core/debounce';

@Component({
    components: { CardComponent, EditToolbar },
})
export default class User extends Vue {

    // Injectectd from url
    @Prop({ default: '' }) private readonly id!: string;

    private selectedPartner: Partner = new Partner();
    private partners: Partner[] = [];
    private total = 0;
    private password = '';
    private showPartnerBox = true;

    private debouncedFetchPartnersAsync = async (search: string): Promise<void> => undefined;

    get roles(): Role[] {
        if (!this.user.roles) return appStore.roles;
        const roleIds = this.user.roles.map((r) => r.id);
        return appStore.roles.filter((role) => roleIds.indexOf(role.id) < 0);
    }

    get user(): ApplicationUser {
        const user = usersStore.user || new ApplicationUser();
        this.selectedPartner = user.partner || new Partner();
        return user;
    }

    get loading(): boolean {
        return appStore.isLoading;
    }

    get isNewUser(): boolean {
        return this.id === Guid.Empty;
    }

    private async created() {
        this.debouncedFetchPartnersAsync = debounce(this.fetchPartnersAsync, 600);
        await usersStore.fetchUserAsync({ id: this.id, asNewIfNotExists: true });
    }

    private async save() {
        if (!this.user.roles || this.user.roles.length === 0) {
            appStore.error({message: 'Selezionare un ruolo dalla lista'});
            return;
        }

        this.$buefy.dialog.confirm({
            title: 'Aggiornamento utente',
            message: 'Sei sicuro/a di voler apportare le modifiche all\'utente selezionato ?',
            confirmText: 'Conferma',
            cancelText: 'Annulla',
            type: 'is-info',
            hasIcon: true,
            onConfirm: async () => {
                await this.saveUser();
            },
        });
    }

    private async saveUser() {

        if (this.isNewUser) {
            await usersStore.createUserAsync(
                {
                    username: this.user.email,
                    email: this.user.email,
                    firstName: this.user.firstName,
                    lastName: this.user.lastName,
                    password: this.password,
                    roles: this.user.roles.map((role) => role.name),
                    partnerId: this.selectedPartner.id,
                })
                .then((result) => {
                    if (result) {
                        this.$router.go(-1);
                    }
                });
        } else {
            await usersStore.updateUserAsync({
                id: this.user.id,
                username: this.user.email,
                email: this.user.email,
                firstName: this.user.firstName,
                lastName: this.user.lastName,
                roles: this.user.roles.map((role) => role.name),
                partnerId: this.selectedPartner.id,
            })
                .then((result) => {
                    if (result) {
                        this.$router.go(-1);
                    }
                });
        }
    }

    private async searchingPartnerAsync(value: string) {
        this.showPartnerBox = false;
        this.debouncedFetchPartnersAsync(value);
    }

    private async fetchPartnersAsync(search: string) {
        if (search.length <= 3) {
            this.partners = [];
            return;
        }
        await partnersService.fetchPartnersAsync(search)
            .then((result) => {
                this.total = result.total || 0;
                this.partners = [];
                result.payload.forEach((partner) => this.partners.push(partner));
            });
    }

    private async changePassword() {

        if (!authenticator.isLoggedIn) return;

        this.$buefy.modal.open({
            props: { id: this.id },
            parent: this,
            component: ChangePassword,
            hasModalCard: true,
            // customClass: 'custom-class custom-class-2',
        });
    }

    private onPartnerSelected(partner: Partner): void {
        this.selectedPartner = partner;
        this.showPartnerBox = true;
    }

    private cancel(): void {
        this.$router.go(-1);
    }

}
